/**
 * Client Portal API client (Module 3.6 — Pilot Run #10 Agent FE-1).
 *
 * The portal is a separate authentication surface from the internal app:
 * portal users (ClientPortalUser) have their own JWT issued under a
 * different audience. This module keeps that token isolated in its own
 * localStorage keys + axios instance so a portal session never collides
 * with an internal Eng-team session opened in the same browser.
 *
 * Endpoints mounted under /api/v1/portal/*:
 *   POST /auth/login                                — anonymous
 *   POST /auth/refresh                              — refresh token
 *   POST /auth/logout                               — bearer
 *   GET  /auth/me                                   — bearer
 *   GET  /projects                                  — bearer
 *   GET  /projects/:id/dashboard                    — bearer + access
 *   GET  /projects/:id/ipcs                         — bearer + access
 *   GET  /projects/:id/ipcs/:ipcId                  — bearer + access
 *   POST /projects/:id/ipcs/:ipcId/approve          — approver
 *   GET  /projects/:id/variations                   — bearer + access
 *   GET  /projects/:id/quality                      — bearer + access
 *   GET  /projects/:id/documents                    — bearer + access
 */
import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'

const API_BASE_URL =
  (import.meta.env.VITE_API_URL as string | undefined) ||
  'http://localhost:8001/api/v1'

const PORTAL_STORAGE = {
  ACCESS: 'portal_access_token',
  REFRESH: 'portal_refresh_token',
  USER: 'portal_user',
} as const

export const portalTokenStore = {
  getAccess(): string | null {
    return localStorage.getItem(PORTAL_STORAGE.ACCESS)
  },
  getRefresh(): string | null {
    return localStorage.getItem(PORTAL_STORAGE.REFRESH)
  },
  set(access: string, refresh: string) {
    localStorage.setItem(PORTAL_STORAGE.ACCESS, access)
    localStorage.setItem(PORTAL_STORAGE.REFRESH, refresh)
  },
  clear() {
    localStorage.removeItem(PORTAL_STORAGE.ACCESS)
    localStorage.removeItem(PORTAL_STORAGE.REFRESH)
    localStorage.removeItem(PORTAL_STORAGE.USER)
  },
  cacheUser(user: PortalUserSelf) {
    localStorage.setItem(PORTAL_STORAGE.USER, JSON.stringify(user))
  },
  getCachedUser(): PortalUserSelf | null {
    const raw = localStorage.getItem(PORTAL_STORAGE.USER)
    if (!raw) return null
    try {
      return JSON.parse(raw) as PortalUserSelf
    } catch {
      return null
    }
  },
}

export const portalApiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
})

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
  _skipAuth?: boolean
}

let isRefreshing = false
let refreshQueue: Array<{
  resolve: (t: string) => void
  reject: (e: unknown) => void
}> = []

function flushQueue(error: unknown, token: string | null) {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error || !token) reject(error)
    else resolve(token)
  })
  refreshQueue = []
}

async function performRefresh(): Promise<string> {
  const refreshToken = portalTokenStore.getRefresh()
  if (!refreshToken) throw new Error('NO_PORTAL_REFRESH_TOKEN')
  const { data } = await axios.post<PortalTokenResponse>(
    `${API_BASE_URL}/portal/auth/refresh`,
    { refresh_token: refreshToken },
    { headers: { 'Content-Type': 'application/json' } },
  )
  portalTokenStore.set(data.access_token, data.refresh_token)
  return data.access_token
}

portalApiClient.interceptors.request.use((config) => {
  const cfg = config as RetryableConfig
  if (!cfg._skipAuth) {
    const token = portalTokenStore.getAccess()
    if (token) {
      cfg.headers.Authorization = `Bearer ${token}`
    }
  }
  return cfg
})

portalApiClient.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as RetryableConfig | undefined
    if (!original || error.response?.status !== 401) {
      return Promise.reject(error)
    }
    if (original._skipAuth || original._retry) {
      return Promise.reject(error)
    }
    if (!portalTokenStore.getRefresh()) {
      portalTokenStore.clear()
      if (
        typeof window !== 'undefined' &&
        !window.location.pathname.startsWith('/portal/login')
      ) {
        window.location.replace('/portal/login')
      }
      return Promise.reject(error)
    }
    original._retry = true
    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        refreshQueue.push({ resolve, reject })
      }).then((t) => {
        original.headers.Authorization = `Bearer ${t}`
        return portalApiClient(original)
      })
    }
    isRefreshing = true
    try {
      const next = await performRefresh()
      flushQueue(null, next)
      original.headers.Authorization = `Bearer ${next}`
      return portalApiClient(original)
    } catch (e) {
      flushQueue(e, null)
      portalTokenStore.clear()
      if (
        typeof window !== 'undefined' &&
        !window.location.pathname.startsWith('/portal/login')
      ) {
        window.location.replace('/portal/login')
      }
      return Promise.reject(e)
    } finally {
      isRefreshing = false
    }
  },
)

// ---------------------------------------------------------------------------
// Schemas (mirrored from backend/app/schemas/portal.py)
// ---------------------------------------------------------------------------

export type PortalAccessRoleStr = 'viewer' | 'approver'

export type PortalNotificationTypeStr =
  | 'ipc.submitted'
  | 'ipc.approved'
  | 'ipc.rejected'
  | 'variation.submitted'
  | 'variation.approved'
  | 'ncr.raised'
  | 'ncr.closed'
  | 'document.shared'
  | 'approval.submitted'
  | 'rfi.submitted'
  | 'rfi.responded'
  | 'progress.update'
  | 'report.published'
  | 'generic'

export interface PortalTokenResponse {
  access_token: string
  refresh_token: string
  token_type: 'bearer'
  expires_in: number
}

export interface PortalUserSelf {
  id: number
  email: string
  name_en: string
  name_ar: string | null
  client_id: number
  last_login_at: string | null
}

export interface PortalProjectSummary {
  id: number
  name: string
  name_ar: string | null
  status: string
  progress_percent: string | number | null
  role: PortalAccessRoleStr
  start_date: string | null
  end_date: string | null
}

export interface PortalProjectDashboard {
  project_id: number
  name: string
  name_ar: string | null
  status: string
  progress_percent: string | number | null
  contract_value: string | number | null
  start_date: string | null
  end_date: string | null
  open_ipcs: number
  open_variations: number
  open_ncrs: number
  shared_documents: number
}

export interface PortalIPCSummary {
  id: number
  ipc_number: string
  period_start: string | null
  period_end: string | null
  status: string
  gross_amount: string | number | null
  net_payable: string | number | null
  submitted_at: string | null
}

export interface PortalIPCDetail extends PortalIPCSummary {
  advance_recovery: string | number | null
  retention_amount: string | number | null
  notes: string | null
}

export interface PortalVariationSummary {
  id: number
  vo_number: string
  title: string
  status: string
  vo_type: string
  submitted_amount: string | number | null
  submitted_at: string | null
}

export interface PortalQualitySummary {
  project_id: number
  open_ncrs: number
  closed_ncrs: number
  open_punches: number
  closed_punches: number
  last_inspection_at: string | null
}

export interface PortalDocumentSummary {
  id: number
  title: string
  category: string
  uploaded_at: string
  file_size_bytes: number | null
  download_url: string | null
}

// === Phase 5-7 schemas ===

export interface PortalBrandingPayload {
  tenant_id: number
  logo_url: string | null
  primary_color: string
  secondary_color: string
  accent_color: string | null
  welcome_message_en: string | null
  welcome_message_ar: string | null
  company_name_en: string | null
  company_name_ar: string | null
  support_email: string | null
  support_phone: string | null
}

export interface PortalNotificationItem {
  id: number
  notification_type: PortalNotificationTypeStr
  title: string
  title_ar: string | null
  message: string
  message_ar: string | null
  link: string | null
  is_read: boolean
  created_at: string
  metadata_json: Record<string, unknown> | null
}

export interface PortalNotificationListResponse {
  items: PortalNotificationItem[]
  unread_count: number
  total: number
}

export type PortalApprovalItemTypeStr = 'vo' | 'milestone'
export type PortalApprovalDecisionStr = 'approved' | 'rejected' | 'conditional'

export interface PortalApprovalDecisionRequest {
  item_type: PortalApprovalItemTypeStr
  item_id: number
  decision: PortalApprovalDecisionStr
  comment?: string
}

export interface PortalApprovalItem {
  id: number
  project_id: number | null
  item_type: PortalApprovalItemTypeStr
  item_id: number
  decision: PortalApprovalDecisionStr
  comment: string | null
  decided_at: string
}

export interface PortalRFISubmitRequest {
  subject: string
  question: string
  priority?: 'low' | 'normal' | 'high' | 'urgent'
  cost_impact_aed?: number
  schedule_impact_days?: number
}

export interface PortalRFISummary {
  id: number
  rfi_number: string
  subject: string
  status: string
  priority: string
  asked_at: string
  answered_at: string | null
  days_open: number
}

export interface PortalRFIResponseItem {
  id: number
  response_text: string
  is_final: boolean
  responded_at: string
}

export interface PortalRFIDetail extends PortalRFISummary {
  question: string
  cost_impact_aed: number | string | null
  schedule_impact_days: number | null
  responses: PortalRFIResponseItem[]
}

export interface PortalProgressPhoto {
  id: number
  title: string | null
  photo_url: string | null
  captured_at: string | null
  caption: string | null
}

export interface PortalProgressUpdate {
  id: number
  title: string
  body: string | null
  progress_percent: string | number | null
  captured_at: string
}

export interface PortalProgressResponse {
  project_id: number
  overall_percent: string | number | null
  last_update_at: string | null
  updates: PortalProgressUpdate[]
  photos: PortalProgressPhoto[]
}

export interface PortalReportSummary {
  id: number
  title: string
  period_label: string | null
  published_at: string
  file_size_bytes: number | null
  download_url: string | null
}

// ---------------------------------------------------------------------------
// API surface
// ---------------------------------------------------------------------------

export const portalApi = {
  async login(email: string, password: string): Promise<PortalTokenResponse> {
    const { data } = await portalApiClient.post<PortalTokenResponse>(
      '/portal/auth/login',
      { email, password },
      { _skipAuth: true } as RetryableConfig,
    )
    portalTokenStore.set(data.access_token, data.refresh_token)
    return data
  },

  async logout(): Promise<void> {
    // Best-effort revoke — the local tokens still get cleared if the
    // server is unreachable (offline / network blip / 5xx).
    try {
      await portalApiClient.post('/portal/auth/logout')
    } catch {
      // swallow — local sign-out always wins
    } finally {
      portalTokenStore.clear()
    }
  },

  async me(): Promise<PortalUserSelf> {
    const { data } = await portalApiClient.get<PortalUserSelf>(
      '/portal/auth/me',
    )
    portalTokenStore.cacheUser(data)
    return data
  },

  async listProjects(): Promise<PortalProjectSummary[]> {
    const { data } = await portalApiClient.get<PortalProjectSummary[]>(
      '/portal/projects',
    )
    return data
  },

  async getDashboard(projectId: number): Promise<PortalProjectDashboard> {
    const { data } = await portalApiClient.get<PortalProjectDashboard>(
      `/portal/projects/${projectId}/dashboard`,
    )
    return data
  },

  async listIPCs(projectId: number): Promise<PortalIPCSummary[]> {
    const { data } = await portalApiClient.get<PortalIPCSummary[]>(
      `/portal/projects/${projectId}/ipcs`,
    )
    return data
  },

  async getIPC(projectId: number, ipcId: number): Promise<PortalIPCDetail> {
    const { data } = await portalApiClient.get<PortalIPCDetail>(
      `/portal/projects/${projectId}/ipcs/${ipcId}`,
    )
    return data
  },

  async approveIPC(
    projectId: number,
    ipcId: number,
    note?: string,
  ): Promise<PortalIPCDetail> {
    const { data } = await portalApiClient.post<PortalIPCDetail>(
      `/portal/projects/${projectId}/ipcs/${ipcId}/approve`,
      { note },
    )
    return data
  },

  async listVariations(
    projectId: number,
  ): Promise<PortalVariationSummary[]> {
    const { data } = await portalApiClient.get<PortalVariationSummary[]>(
      `/portal/projects/${projectId}/variations`,
    )
    return data
  },

  async getQualitySummary(
    projectId: number,
  ): Promise<PortalQualitySummary> {
    const { data } = await portalApiClient.get<PortalQualitySummary>(
      `/portal/projects/${projectId}/quality`,
    )
    return data
  },

  async listDocuments(
    projectId: number,
  ): Promise<PortalDocumentSummary[]> {
    const { data } = await portalApiClient.get<PortalDocumentSummary[]>(
      `/portal/projects/${projectId}/documents`,
    )
    return data
  },

  // ===== Phase 5-7 endpoints =====

  async getBrandingPublic(tenantId?: number): Promise<PortalBrandingPayload> {
    const url = tenantId
      ? `/portal/branding?tenant=${tenantId}`
      : '/portal/branding'
    const { data } = await portalApiClient.get<PortalBrandingPayload>(
      url,
      { _skipAuth: true } as RetryableConfig,
    )
    return data
  },

  async getBrandingMe(): Promise<PortalBrandingPayload> {
    const { data } = await portalApiClient.get<PortalBrandingPayload>(
      '/portal/branding/me',
    )
    return data
  },

  async listNotifications(opts?: {
    projectId?: number
    unreadOnly?: boolean
    limit?: number
  }): Promise<PortalNotificationListResponse> {
    const params = new URLSearchParams()
    if (opts?.projectId) params.set('project_id', String(opts.projectId))
    if (opts?.unreadOnly) params.set('unread_only', 'true')
    if (opts?.limit) params.set('limit', String(opts.limit))
    const qs = params.toString()
    const { data } = await portalApiClient.get<PortalNotificationListResponse>(
      `/portal/notifications${qs ? `?${qs}` : ''}`,
    )
    return data
  },

  async markNotificationRead(id: number): Promise<void> {
    await portalApiClient.post(`/portal/notifications/${id}/read`)
  },

  async markAllNotificationsRead(): Promise<{ updated: number }> {
    const { data } = await portalApiClient.post<{ updated: number }>(
      '/portal/notifications/read-all',
    )
    return data
  },

  async submitApproval(
    projectId: number,
    payload: PortalApprovalDecisionRequest,
  ): Promise<PortalApprovalItem> {
    const { data } = await portalApiClient.post<PortalApprovalItem>(
      `/portal/projects/${projectId}/approvals`,
      payload,
    )
    return data
  },

  async listApprovals(projectId: number): Promise<PortalApprovalItem[]> {
    const { data } = await portalApiClient.get<{
      items: PortalApprovalItem[]
      total: number
    }>(`/portal/projects/${projectId}/approvals`)
    return data.items
  },

  async submitRFI(
    projectId: number,
    payload: PortalRFISubmitRequest,
  ): Promise<PortalRFISummary> {
    const { data } = await portalApiClient.post<PortalRFISummary>(
      `/portal/projects/${projectId}/rfis`,
      payload,
    )
    return data
  },

  async listRFIs(projectId: number): Promise<PortalRFISummary[]> {
    const { data } = await portalApiClient.get<PortalRFISummary[]>(
      `/portal/projects/${projectId}/rfis`,
    )
    return data
  },

  async getRFI(
    projectId: number,
    rfiId: number,
  ): Promise<PortalRFIDetail> {
    const { data } = await portalApiClient.get<PortalRFIDetail>(
      `/portal/projects/${projectId}/rfis/${rfiId}`,
    )
    return data
  },

  async getProgress(projectId: number): Promise<PortalProgressResponse> {
    const { data } = await portalApiClient.get<PortalProgressResponse>(
      `/portal/projects/${projectId}/progress`,
    )
    return data
  },

  async listReports(projectId: number): Promise<PortalReportSummary[]> {
    const { data } = await portalApiClient.get<PortalReportSummary[]>(
      `/portal/projects/${projectId}/reports`,
    )
    return data
  },
}

export function isPortalAuthenticated(): boolean {
  return Boolean(portalTokenStore.getAccess())
}
